import React, { useMemo } from 'react'
import { BreadcrumbView } from '../../../../../../context/breadcrumb.context'
import { CardFullHeightScrollY } from '../../../../../../shared/card/card-full-height-scroll-y'
import { IconCheck, IconDownload, IconVersion } from '../../../../../../shared/icon/icon'
import { TableAgClient } from '../../../../../../shared/table-builder/table-ag-client'
import { useDownloadPresign } from '../../../../../../shared/download/hook'
import { useApiFormAsset, useApiFormPublishAssetVersion, useApiListPublishAssetVersion } from '../../../../../../api-new/state/content'
import { ColumnDateTime, ColumnFormat, ColumnMenu, ColumnSize, ColumnText, ColumnYesNo } from '../../../../../../shared/table-builder/table-columns'
import { Menu, MenuDelete, MenuEdit, MenuItem, MenuRecycle } from '../../../../../../shared/menu/menu'
import { useRoleOrgNode } from '../../../../../../role/hook/role-org'
import { useParamsInt } from '../../../../../../shared/hooks/location'
import { useRoleSysApp } from '../../../../../../role/hook/role-sys'
import { Route, useRouteMatch } from 'react-router-dom/cjs/react-router-dom.min'
import { PublishAssetVersionEdit } from './publish-asset-version-edit'
import { PublishAssetVersionListRecycle } from './publish-asset-version-list-recycle'
import { Delete } from '../../../../../../shared/page/page-delete'
import { versionPollInterval } from './version-poll'

const formatBytes = (bytes) => {
  if (!bytes || bytes === 0) return '0 KB'
  if (bytes >= 1073741824) return `${(bytes / 1073741824).toFixed(2)} GB`
  if (bytes >= 1048576) return `${(bytes / 1048576).toFixed(2)} MB`
  return `${(bytes / 1024).toFixed(2)} KB`
}

export const PublishAssetVersionlist = () => {
  const { url } = useRouteMatch()

  const { assetID } = useParamsInt()

  const apiFormAsset = useApiFormAsset()
  const apiListPublishAssetVersion = useApiListPublishAssetVersion()

  const roleOrgNode = useRoleOrgNode()

  const { active, download } = useDownloadPresign()

  // The file's name, for the title and the crumb: read whenever the URL is
  // at or under this list -- a version's own page is opened straight from
  // the audit log (owner 2026-10-09), where the list (and its grid's
  // onReady) never shows.
  const handlePath = () => apiFormAsset.get(assetID)

  const handleReady = () => apiListPublishAssetVersion.get(`${assetID}/asset`)
  const handleDestroyed = () => apiListPublishAssetVersion.init()

  const handleSetCurrent = async id => {
    await apiListPublishAssetVersion.put(`${id}/current`)
    await apiListPublishAssetVersion.get(`${assetID}/asset`)
  }

  const handleDownload = async id => {
    let presigned = await apiListPublishAssetVersion.get(`${id}/presigned-download`)
    download(presigned.url)
  }

  const roleSysApp = useRoleSysApp()

  const name = apiFormAsset.form.name
  const title = useMemo(() => (name ? `${name} versions` : 'versions'), [name])

  const HeaderMenu = () =>
    <Menu>
      <MenuRecycle to={`${url}/${assetID}/recycle`} show={true} />
    </Menu>

  return (
    <React.Fragment>
      <BreadcrumbView Icon={IconVersion} text={title} onPath={handlePath}>
        <CardFullHeightScrollY title={title} HeaderMenu={HeaderMenu} sortCol="versionCount" sortDesc>
          <TableAgClient apiList={apiListPublishAssetVersion} sortCol="versionCount" sortDesc onReady={handleReady} onDestoryed={handleDestroyed} pollInterval={versionPollInterval(apiListPublishAssetVersion.items)}>
            <ColumnText field="versionCount" header="Version" />
            <ColumnDateTime field="createdAt" />
            <ColumnFormat field="contentLength" header="Size" render={row => {
              const d = row.data
              if (d.status === 'complete' || !d.bytesTransferred) return formatBytes(d.contentLength)
              return `${formatBytes(d.bytesTransferred)} / ${formatBytes(d.contentLength)}`
            }} />
            <ColumnYesNo field="isCurrent" header="Active" />
            <ColumnText field="uploadedBy" />
            <ColumnText field="note" header="Notes" />
            <ColumnText field="status" readonly />
            <ColumnText field="statusReason" header="Reason" />
            <ColumnText field="statusMessage" header="Message" />
            <ColumnMenu
              menu={row =>
                <Menu small>
                  <MenuItem
                    Icon={IconCheck}
                    text="set active"
                    onClick={() => handleSetCurrent(row.id)}
                    show={roleOrgNode.accessUpdate}
                    disabled={row.status !== 'complete'}
                  />
                  <MenuItem Icon={IconDownload} text="download" onClick={() => handleDownload(row.id)} show={roleOrgNode.accessRead} disabled={active} />
                  <MenuEdit to={`version/${row.id}/edit`} show={roleSysApp.accessUpdate} />
                  <MenuDelete to={`version/${row.id}/delete`} show={roleOrgNode.accessDelete} />
                </Menu>}
            />
          </TableAgClient>
        </CardFullHeightScrollY>
      </BreadcrumbView>

      <Route path={`${url}/:assetVersionID/edit`}>
        <PublishAssetVersionEdit parentURL={url} />
      </Route>

      <Route path={`${url}/:assetID/recycle`}>
        <PublishAssetVersionListRecycle />
      </Route>

      <Route path={`${url}/:deleteID/delete`}>
        <Delete useApiForm={useApiFormPublishAssetVersion} titleParam="filename" successPath={url} />
      </Route>
    </React.Fragment>
  )
}
